import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Layers,
  BookOpen,
  FolderTree,
  ArrowLeft,
  Pencil,
  Trash2,
  Plus,
  ChevronRight,
  CheckSquare,
  Square,
  Download,
  Upload
} from 'lucide-react'

import { resolveBackNavigation } from '@/lib/utils'
import { ImportStandardModal } from '@/components/admin/ImportStandardModal'


interface MasterChapter {
  id: number
  name: string
  number: number | null
}

interface MasterSubject {
  id: number
  name: string
  code: string
  chapters: MasterChapter[]
}

interface StandardChapter {
  id: number
  name: string
  number: number | null
  sections_count: number
}

interface StandardSubject {
  id: number
  name: string
  code: string
  chapters: StandardChapter[]
}

interface StandardDetail {
  id: number
  name: string
  sort_order: number
}

interface Props {
  standard: StandardDetail
  standard_subjects: StandardSubject[]
  master_subjects: MasterSubject[]
}

export default function StandardShow({ standard, standard_subjects, master_subjects }: Props) {
  // Edit Standard Modal
  const [editStandardModalOpen, setEditStandardModalOpen] = useState(false)
  const [standardName, setStandardName] = useState(standard.name)
  const [standardSortOrder, setStandardSortOrder] = useState<number | ''>(standard.sort_order)

  // Add Subject Modal
  const [addSubjectModalOpen, setAddSubjectModalOpen] = useState(false)
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | ''>('')
  const [selectedChapterIdsForSubject, setSelectedChapterIdsForSubject] = useState<number[]>([])

  // Add Chapter Modal
  const [addChapterModalOpen, setAddChapterModalOpen] = useState(false)
  const [targetSubjectIdForChapter, setTargetSubjectIdForChapter] = useState<number | null>(null)
  const [selectedChapterIdToAdd, setSelectedChapterIdToAdd] = useState<number | ''>('')

  // Import Modal
  const [importModalOpen, setImportModalOpen] = useState(false)


  // Summary counts
  const totalChapters = standard_subjects.reduce((acc, s) => acc + s.chapters.length, 0)

  // Standard details save
  const handleSaveStandardDetails = (e: React.FormEvent) => {
    e.preventDefault()
    if (!standardName.trim()) return

    router.patch(`/admin/standards/${standard.id}`, {
      standard: {
        name: standardName.trim(),
        sort_order: standardSortOrder === '' ? 0 : standardSortOrder
      }
    }, {
      onSuccess: () => setEditStandardModalOpen(false)
    })
  }

  // Add Subject Flow
  const openAddSubjectModal = () => {
    // Pick the first master subject not yet fully added, or just first subject
    const firstSubject = master_subjects[0]
    if (firstSubject) {
      setSelectedSubjectId(firstSubject.id)
      setSelectedChapterIdsForSubject(firstSubject.chapters.map((c) => c.id))
    } else {
      setSelectedSubjectId('')
      setSelectedChapterIdsForSubject([])
    }
    setAddSubjectModalOpen(true)
  }

  const handleSubjectChange = (subjectId: number) => {
    setSelectedSubjectId(subjectId)
    const masterSub = master_subjects.find((s) => s.id === subjectId)
    if (masterSub) {
      setSelectedChapterIdsForSubject(masterSub.chapters.map((c) => c.id))
    } else {
      setSelectedChapterIdsForSubject([])
    }
  }

  const handleToggleChapterInSubjectModal = (chapId: number) => {
    if (selectedChapterIdsForSubject.includes(chapId)) {
      setSelectedChapterIdsForSubject(selectedChapterIdsForSubject.filter((id) => id !== chapId))
    } else {
      setSelectedChapterIdsForSubject([...selectedChapterIdsForSubject, chapId])
    }
  }

  const handleAddSubjectSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSubjectId) return

    router.post(`/admin/standards/${standard.id}/add_subject`, {
      subject_id: selectedSubjectId,
      chapter_ids: selectedChapterIdsForSubject
    }, {
      onSuccess: () => setAddSubjectModalOpen(false)
    })
  }

  // Remove Subject Flow
  const handleRemoveSubject = (subjectId: number, subjectName: string) => {
    if (!confirm(`Are you sure you want to remove ${subjectName} and all its chapters from Standard ${standard.name}?`)) return
    router.delete(`/admin/standards/${standard.id}/remove_subject`, {
      data: { subject_id: subjectId }
    })
  }

  // Add Chapter Flow
  const openAddChapterModal = (subjectId: number) => {
    setTargetSubjectIdForChapter(subjectId)
    const masterSub = master_subjects.find((s) => s.id === subjectId)
    const existingInStandard = standard_subjects.find((s) => s.id === subjectId)?.chapters.map((c) => c.id) || []
    const availableChaps = masterSub?.chapters.filter((c) => !existingInStandard.includes(c.id)) || []

    if (availableChaps.length > 0) {
      setSelectedChapterIdToAdd(availableChaps[0].id)
    } else {
      setSelectedChapterIdToAdd('')
    }
    setAddChapterModalOpen(true)
  }

  const handleAddChapterSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedChapterIdToAdd) return

    router.post(`/admin/standards/${standard.id}/add_chapter`, {
      chapter_id: selectedChapterIdToAdd
    }, {
      onSuccess: () => setAddChapterModalOpen(false)
    })
  }

  // Remove Chapter Flow
  const handleRemoveChapter = (chapterId: number, chapterName: string) => {
    if (!confirm(`Remove "${chapterName}" from Standard ${standard.name}?`)) return
    router.delete(`/admin/standards/${standard.id}/remove_chapter`, {
      data: { chapter_id: chapterId }
    })
  }

  const selectedMasterSubject = master_subjects.find((s) => s.id === selectedSubjectId)
  const targetMasterSubjectForChapter = master_subjects.find((s) => s.id === targetSubjectIdForChapter)
  const existingChapterIdsInTarget =
    standard_subjects.find((s) => s.id === targetSubjectIdForChapter)?.chapters.map((c) => c.id) || []
  const availableChaptersToAdd =
    targetMasterSubjectForChapter?.chapters.filter((c) => !existingChapterIdsInTarget.includes(c.id)) || []

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/standards', 'Standards')

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href={backHref}>
                <ArrowLeft className="mr-1.5 h-4 w-4" /> {backLabel}
              </Link>
            </Button>
            <span className="text-muted-foreground">/</span>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">Standard: {standard.name}</h1>
              <Badge variant="outline" className="text-xs">
                Sort Order: {standard.sort_order}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 font-medium"
              onClick={() => setImportModalOpen(true)}
            >
              <Upload className="h-3.5 w-3.5 text-muted-foreground" />
              Import JSON
            </Button>
            <Button variant="outline" size="sm" asChild className="gap-1.5 font-medium">
              <a href={`/admin/standards/${standard.id}/export`}>
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
                Export JSON
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditStandardModalOpen(true)} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Edit Standard
            </Button>
            <Button size="sm" onClick={openAddSubjectModal} className="gap-1.5 font-semibold">
              <Plus className="h-4 w-4" /> Add Subject
            </Button>
          </div>

        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Subjects in Standard</p>
                <p className="text-2xl font-bold mt-0.5">{standard_subjects.length}</p>
              </div>
              <BookOpen className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Chapters in Standard</p>
                <p className="text-2xl font-bold mt-0.5">{totalChapters}</p>
              </div>
              <FolderTree className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
        </div>

        {/* Subjects & Chapters List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" /> Curriculum Structure
            </h2>
          </div>

          {standard_subjects.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <Layers className="h-10 w-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">No subjects or chapters associated with Standard {standard.name} yet.</p>
                <Button size="sm" onClick={openAddSubjectModal} className="gap-1.5">
                  <Plus className="h-4 w-4" /> Add Subject from Master Taxonomy
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {standard_subjects.map((subj) => (
                <Card key={subj.id} className="border-2 shadow-sm">
                  {/* Subject Header */}
                  <CardHeader className="bg-muted/40 border-b py-3 px-5 flex flex-row items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        {subj.name}
                      </CardTitle>
                      <Badge variant="outline" className="font-mono text-xs uppercase bg-background">
                        {subj.code}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {subj.chapters.length} {subj.chapters.length === 1 ? 'Chapter' : 'Chapters'}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openAddChapterModal(subj.id)}
                        className="gap-1 text-xs font-semibold h-7"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Chapter
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleRemoveSubject(subj.id, subj.name)}
                        title={`Remove ${subj.name} from Standard`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardHeader>

                  {/* Chapters List */}
                  <CardContent className="p-4">
                    {subj.chapters.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic text-center py-2">
                        No chapters added under {subj.name} for this standard. Click "Add Chapter" above to associate one.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                        {subj.chapters.map((chap) => (
                          <div
                            key={chap.id}
                            className="flex items-center justify-between rounded-lg border bg-card p-3 shadow-xs hover:border-primary/40 transition-colors"
                          >
                            <div className="space-y-0.5 truncate pr-2">
                              <p className="text-xs font-medium truncate flex items-center gap-1.5">
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                {chap.number !== null ? `#${chap.number} ` : ''}
                                {chap.name}
                              </p>
                              <p className="text-[10px] text-muted-foreground pl-5">
                                {chap.sections_count} {chap.sections_count === 1 ? 'section' : 'sections'}
                              </p>
                            </div>

                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-destructive shrink-0"
                              onClick={() => handleRemoveChapter(chap.id, chap.name)}
                              title="Remove Chapter from Standard"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Modal 1: Edit Standard Details */}
        <Dialog open={editStandardModalOpen} onOpenChange={setEditStandardModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Standard</DialogTitle>
              <DialogDescription>Update standard title and display sort order.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveStandardDetails} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Standard Name *</label>
                <Input
                  value={standardName}
                  onChange={(e) => setStandardName(e.target.value)}
                  placeholder="e.g. XI"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Sort Order</label>
                <Input
                  type="number"
                  value={standardSortOrder}
                  onChange={(e) => setStandardSortOrder(e.target.value ? Number(e.target.value) : '')}
                  placeholder="0"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditStandardModalOpen(false)}>Cancel</Button>
                <Button type="submit">Save Changes</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal 2: Add Subject from Master Taxonomy */}
        <Dialog open={addSubjectModalOpen} onOpenChange={setAddSubjectModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Subject to Standard</DialogTitle>
              <DialogDescription>Select a master subject and choose which chapters to include.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSubjectSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Subject *</label>
                <select
                  value={selectedSubjectId}
                  onChange={(e) => handleSubjectChange(Number(e.target.value))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  required
                >
                  <option value="" disabled>-- Select a Master Subject --</option>
                  {master_subjects.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name} ({sub.code})
                    </option>
                  ))}
                </select>
              </div>

              {selectedMasterSubject && selectedMasterSubject.chapters.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">
                      Chapters to Include ({selectedChapterIdsForSubject.length} / {selectedMasterSubject.chapters.length})
                    </label>
                    <div className="flex gap-2 text-xs">
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setSelectedChapterIdsForSubject(selectedMasterSubject.chapters.map((c) => c.id))}
                      >
                        Select All
                      </button>
                      <span>|</span>
                      <button
                        type="button"
                        className="text-muted-foreground hover:underline"
                        onClick={() => setSelectedChapterIdsForSubject([])}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-1 p-2 border rounded-md bg-muted/20">
                    {selectedMasterSubject.chapters.map((chap) => {
                      const isChecked = selectedChapterIdsForSubject.includes(chap.id)
                      return (
                        <label
                          key={chap.id}
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-muted text-xs cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleChapterInSubjectModal(chap.id)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="truncate">
                            {chap.number !== null ? `#${chap.number} ` : ''}
                            {chap.name}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setAddSubjectModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={!selectedSubjectId}>Add Subject</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal 3: Add Chapter to Subject */}
        <Dialog open={addChapterModalOpen} onOpenChange={setAddChapterModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Chapter to {targetMasterSubjectForChapter?.name}</DialogTitle>
              <DialogDescription>Select from available master chapters for {targetMasterSubjectForChapter?.name}.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddChapterSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Available Chapters *</label>
                {availableChaptersToAdd.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic py-2">
                    All master chapters of {targetMasterSubjectForChapter?.name} are already associated with this standard.
                  </p>
                ) : (
                  <select
                    value={selectedChapterIdToAdd}
                    onChange={(e) => setSelectedChapterIdToAdd(Number(e.target.value))}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    required
                  >
                    {availableChaptersToAdd.map((chap) => (
                      <option key={chap.id} value={chap.id}>
                        {chap.number !== null ? `#${chap.number} ` : ''}
                        {chap.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setAddChapterModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={!selectedChapterIdToAdd || availableChaptersToAdd.length === 0}>
                  Add Chapter
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Modal */}
        <ImportStandardModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
          targetStandardId={standard.id}
        />
      </div>
    </AppShell>
  )
}

